import { yupResolver } from "@hookform/resolvers/yup";
import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { ComponentsCustomerType } from "@omni/types/components/customer/shared/customerType.enum";
import { FormProvider } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { CustomerDetailsFormBlock } from "@/components/Form/RHF/FormBlocks/Customer/customerFormBlock.container";
import {
    CustomerFormBlockFormValues,
    CustomerFormBlockValidationSchema
} from "@/components/Form/RHF/FormBlocks/Customer/customerFormBlock.schema";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Modal } from "@/components/General/Modal/modal";
import { isLoading } from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import { CheckoutCustomerFormValues } from "../../checkoutCustomer.schema";

const FORM_ID = "customerDetailsModal";

type CustomerDetailsModalProps = {
    customer: ComponentsCustomer;
    displayModal: boolean;
    onDisplayModal: (show: boolean) => void;
    modalSubmit: (
        formObject: CustomerFormBlockFormValues
    ) => Promise<TApiAction<any>>;
};

const CustomerDetailsModal = ({
    customer,
    displayModal,
    onDisplayModal,
    modalSubmit
}: CustomerDetailsModalProps) => {
    const { t } = useGranite();
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const isCustomerLoading = useSelector(isLoading);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            customerType: customer.customerType,
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone || "",
            phonePrefix: customer.phonePrefix || "",
            vatInfo:
                customer.customerType === ComponentsCustomerType.B2B
                    ? {
                          vatNr: customer.vatNr || "",
                          companyName: customer.companyName || "",
                          vatNrPrefix: customer.vatNrPrefix || ""
                      }
                    : undefined
        },
        resolver: yupResolver(CustomerFormBlockValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const modalSubmitHandler = (values: CustomerFormBlockFormValues) => {
        modalSubmit(values).then(() => onDisplayModal(false));
    };

    return (
        <Modal
            title={t("general.customerDetail.modal.title")}
            subtitle={t("general.customerDetail.modal.suttitle")}
            formId={FORM_ID}
            open={displayModal}
            setOpen={onDisplayModal}
            confirmButtonText={t("general.customerDetail.modal.button.label")}
            isLoading={isCustomerLoading}
        >
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(modalSubmitHandler)}
                        >
                            <CustomerDetailsFormBlock<CheckoutCustomerFormValues> />
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { CustomerDetailsModal };
